
import React, { useState } from 'react';
import Button from './Button';

const StorageRulesHelpModal = ({ isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) {
    return null;
  }

  const storageRules = `rules_version = '2';
service firebase.storage {
  match /b/{bucket}/o {
    // Allow users to manage their own files within an 'essays' folder
    match /essays/{userId}/{allPaths=**} {
      allow read, write: if request.auth != null && request.auth.uid == userId;
    }
  }
}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(storageRules).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    });
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex justify-center items-center p-4" 
      aria-modal="true" 
      role="alertdialog"
    >
      <div className="bg-white rounded-lg shadow-2xl p-6 md:p-8 w-full max-w-3xl mx-auto space-y-6">
        <div className="text-center">
            <svg className="w-16 h-16 mx-auto text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h2 className="mt-4 text-2xl font-extrabold text-secondary-800">Action Required: Configure Storage Security Rules</h2>
        </div>

        <p className="text-base text-secondary-600 text-center">
          It looks like you're still running into a permission issue when uploading files. This is almost always caused by incorrect Firebase Storage security rules. Let's walk through fixing this step-by-step.
        </p>

        <div className="space-y-4 text-left">
          <h3 className="text-lg font-semibold text-secondary-700">How to Fix This:</h3>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-lg">1</div>
            <div>
              <h4 className="font-semibold text-secondary-800">Go to Firebase Console</h4>
              <p className="text-sm text-secondary-600">
                Navigate to your Firebase project, then go to the <span className="font-bold">Storage</span> section and click on the <span className="font-bold">Rules</span> tab.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-lg">2</div>
            <div>
              <h4 className="font-semibold text-secondary-800">Update Your Rules</h4>
              <p className="text-sm text-secondary-600">
                Replace the existing rules with the following. This ruleset allows any signed-in user to upload, view, and delete files that are stored under their own unique user ID, which is a secure practice.
              </p>
              <div className="relative mt-2">
                <pre className="text-sm bg-secondary-900 text-white p-3 rounded-md overflow-x-auto pr-16 whitespace-pre-wrap break-words">
                  <code>
                    {storageRules}
                  </code>
                </pre>
                <button 
                  onClick={handleCopy} 
                  className="absolute top-2 right-2 bg-secondary-700 text-white px-3 py-1 text-xs font-semibold rounded hover:bg-secondary-600 transition-colors"
                  aria-label="Copy rules to clipboard"
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
           <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center font-bold text-lg">3</div>
            <div>
              <h4 className="font-semibold text-secondary-800">Publish Changes</h4>
              <p className="text-sm text-secondary-600">
                Click the blue <strong className="font-semibold">"Publish"</strong> button. The changes may take a minute to take effect.
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800">
          <h4 className="font-bold">Still not working? Troubleshooting Checklist:</h4>
          <ul className="list-disc list-inside mt-2 text-sm space-y-1">
            <li>Did you paste the rules in the correct place? It must be in <strong className="font-semibold">Storage &gt; Rules</strong>, not Firestore Database &gt; Rules.</li>
            <li>Did you remember to click the blue <strong className="font-semibold">"Publish"</strong> button after pasting the rules? Changes are not saved otherwise.</li>
            <li>It can sometimes take up to a minute for new rules to become active. Try waiting 60 seconds after publishing and then attempt the upload again.</li>
          </ul>
        </div>
        
        <div className="mt-8 flex justify-center">
          <Button variant="primary" size="lg" onClick={onClose}>
            I have double-checked the rules, let's try again
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StorageRulesHelpModal;